'use client'

import { useQuery } from '@apollo/client'
import { useActiveChannelSnapshot } from '@chain-zen/chat'
import type { LucideProps } from '@chain-zen/ui'
import { Avatar, Card, CardContent, CardHeader, CardTitle, CheckCircle, Mail, Phone, UserCog } from '@chain-zen/ui'
import type { ForwardRefExoticComponent, RefAttributes } from 'react'
import React from 'react'

import { USER } from '../../data/queries/user'
import type { IChatUser } from '../../types/chat-types'
import type { UserReturnType } from '../../types/user'
import { normalizeChatUser } from '../../utils/chat-utils'
import { roles } from '../../utils/user'
import { UserAvatar } from '../shared/user/user-avatar'
import { CompanyDetails } from './company-details'

/**
 * Displays chat information with an icon and text.
 */
const ChatInfo = ({
  info,
  icon: Icon
}: {
  icon: ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>>
  info: string
}) => {
  return (
    <div className="flex space-x-2 items-center">
      <Icon className="h-4 w-4" />
      <span className={`text-sm`}>{info}</span>
    </div>
  )
}

/**
 * Displays details of a direct chat between two users.
 */
export const DirectChatDetails = () => {
  const { currentUserID, members } = useActiveChannelSnapshot()

  //userID of the user with whom the chat is initiated
  const connectedUser = members.find((member) => member.id !== currentUserID)
  const user = (connectedUser?.user ?? null) as IChatUser | null

  const chatDetails = React.useMemo(() => {
    const normalizedUser = user ? normalizeChatUser(user) : null
    const userRoleLabel = roles.find((role) => role.value === user?.user_role)?.label || ''

    return {
      ...normalizedUser,
      userRole: (user?.user_role && userRoleLabel) || ''
    }
  }, [user])

  /** Query the other member if it is 1on1 */
  const { data } = useQuery<{ user: UserReturnType | null }>(USER, {
    fetchPolicy: 'cache-first',
    variables: { id: chatDetails.id }
  })

  const chatRecipientMembership = data?.user?.primaryMembership
  const company = chatRecipientMembership?.workspace?.company

  return (
    <div className="flex flex-col p-4 gap-4">
      <Card>
        <CardHeader>
          <CardTitle>
            <div className="flex items-center gap-2">
              <UserAvatar
                size="sm"
                user={chatDetails}
                workspace={chatRecipientMembership?.workspace}
                showTooltip={false}
                showOnlineBadge={false}
              />
              <span>{chatDetails.name}</span>
              <span>
                {!!chatDetails.isVerified && (
                  <CheckCircle className="text-green-500" style={{ height: `16px`, width: `16px` }} />
                )}
              </span>
            </div>
          </CardTitle>
        </CardHeader>

        <CardContent>
          <div className="flex flex-col gap-4">
            {!!chatDetails.email && <ChatInfo info={chatDetails.email} icon={Mail} />}
            {!!chatDetails.phone && <ChatInfo info={chatDetails.phone} icon={Phone} />}
            {!!chatDetails.userRole && <ChatInfo info={chatDetails.userRole} icon={UserCog} />}
            <div className="flex items-center gap-2">
              {!!chatRecipientMembership?.workspace && (
                <>
                  <Avatar
                    src={chatRecipientMembership?.workspace?.logo || ''}
                    alt={chatRecipientMembership?.workspace?.displayName || ''}
                    name={chatRecipientMembership?.workspace?.displayName || ''}
                    variant="square"
                    size="xs"
                  />
                  <span className="text-sm font-semibold">{chatRecipientMembership?.workspace?.displayName}</span>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Only show Company Details if company is verified */}
      {!!chatRecipientMembership?.workspace?.isVerified && !!company && <CompanyDetails company={company} />}
    </div>
  )
}
